import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

import QuestionnaireRenderer from "../../AnswerQuestionnaire";
import QuestionnairePickerModal from "../../AnswerQuestionnaire/components/QuestionnairePickerModal";
import useQuestionnaires from "../../AnswerQuestionnaire/hooks/useQuestionnaire";
import "./QuestionnaireSection.css";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import RootContext from '@/RootContext';

dayjs.extend(relativeTime);
const purple = "#2E2868";

export default function QuestionnaireSection({ subjectId }) {
  const {
    questionnaires,
    activeQuestionnaires,
    showModal,
    loading,
    collapsedMap,
    toggleCollapse,
    openQuestionnaireModal,
    closeModal,
    onAnswer,
    setActiveQuestionnaires,
    handleSelectQuestionnaire,
    handleRemoveQuestionnaire
  } = useQuestionnaires({ subjectId });

  const rootContext = useContext(RootContext);
  const auth = rootContext.get('auth');

  return (
    <div className="questionnaire-section">
      <div className="section-header text-end mt-3">
        <button
          onClick={openQuestionnaireModal}
          className="btn btn-sm"
          style={{
            border: `1px dashed ${purple}`,
            color: purple,
            borderRadius: "20px",
            padding: "5px 20px",
            backgroundColor: "transparent",
          }}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Questionnaire
        </button>
      </div>

      {activeQuestionnaires.length === 0 && (
        <p className="empty-text">No questionnaires added yet.</p>
      )}

      {activeQuestionnaires.map((q, index) => {
        const key = `${q.source}`;

        const isCollapsed = collapsedMap[key];


        const answeredCount = q.answers?.length || 0;

        const resultLabel =
          q.outcome ??
          (q.answeredCount < q.totalQuestions
            ? "In progress"
            : "Unknown");


        return (
          <div
            key={key}
            className="questionnaire-wrapper-card"
          >
            {/* ---------- Expandable Header ---------- */}
            <div
              className="questionnaire-delete-container"
            >
              <div
                className="questionnaire-title collapsible-header"
                onClick={() => toggleCollapse(key)}
              >
                <div className="questionnaire-title-text">
                  <div className="questionnaire-name">
                    {q.name || `Questionnaire ${index + 1}`}
                  </div>

                  <div className="questionnaire-meta">
                    <span>
                      Last updated {dayjs(q.lastUpdatedAt).fromNow()}
                    </span>

                    <span className="meta-separator">|</span>

                    <span>
                      {answeredCount}/{q.totalQuestions}
                    </span>

                    <span className="meta-separator">|</span>

                    <span
                      className={`questionnaire-result`}
                    >
                      {resultLabel}
                    </span>
                  </div>
                </div>

                <div
                  className="collapse-icon"
                  style={{
                    transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                  }}
                >
                  <i className="bi bi-chevron-right" />
                </div>
              </div>
              {auth?.groups?.includes('admin') && q.answers?.length > 0 && <div
                className="remove-icon"
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  style={{ cursor: 'pointer', fontSize: '16px', color: purple }}
                  title="Remove Source"
                  onClick={() => handleRemoveQuestionnaire(q.source, q.answers)}
                />
              </div>}
            </div>

            {/* ---------- Body ---------- */}
            {!isCollapsed && (
              <QuestionnaireRenderer
                questionnaire={q}
                answers={q.answers}
                source={q.source}
                onAnswer={onAnswer}
                oldMachineState={q.machineState}
                oldMachineResponse={q.response}
                totalQuestions={q.totalQuestions}
                answeredCount={q.answeredCount}
                onResult={(result) => {
                  setActiveQuestionnaires(prev =>
                    prev.map(item =>
                      item.id === q.id && item.source === q.source
                        ? {
                          ...item,
                          outcome: result.title,
                          outcome_description: result.description
                        }
                        : item
                    )
                  );
                }}
              />
            )}
          </div>
        );
      })}

      {/* ---------------- Select Questionnaire Modal ---------------- */}
      {showModal && (
        <QuestionnairePickerModal
          loading={loading}
          questionnaires={questionnaires}
          onClose={closeModal}
          onSelect={handleSelectQuestionnaire}
        />
      )}
    </div>
  );
}
